import React, { useEffect } from 'react';
import { ProjectItem } from '../../types';
import './projectmodel.css';

const caseStudies: Record<number, {
  client: string;
  industry: string;
  projectType: string;
  outcome: string[];
  challenge: string;
  solution: string;
  stack: string[];
}> = {
  1: {
    client: 'Manufacturing Organisation',
    industry: 'Manufacturing',
    projectType: 'Computer Vision Solution',
    outcome: [
      'Automated defect detection on production lines',
      'Real-time quality monitoring and alerts',
      'Reduced dependency on manual inspection',
      'Improved overall operational efficiency',
    ],
    challenge:
      'The organisation relied on manual inspection processes that were time-consuming and prone to inconsistencies, making it difficult to maintain quality standards at scale.',
    solution:
      'Deployed an AI-powered visual inspection system that continuously monitors production lines, automatically identifies defects, and triggers alerts — enabling faster corrective action.',
    stack: ['Computer Vision', 'Deep Learning', 'Edge Inference', 'Web Dashboard'],
  },

  2: {
    client: 'Retail Organisation',
    industry: 'Retail & Distribution',
    projectType: 'Business Intelligence Platform',
    outcome: [
      'Unified view of business data across systems',
      'Real-time dashboards for operations and sales',
      'Faster and more reliable reporting',
      'Improved decision-making across teams',
    ],
    challenge:
      'Business data was siloed across multiple platforms, making it difficult to generate timely reports and get a clear picture of overall performance.',
    solution:
      'Built a centralised BI platform that integrates data from multiple sources and delivers interactive dashboards — giving teams instant access to the insights they need.',
    stack: ['Business Intelligence', 'Data Integration', 'Analytics', 'Cloud Services'],
  },

  3: {
    client: 'Healthcare Organisation',
    industry: 'Healthcare',
    projectType: 'Medical Image Analysis Solution',
    outcome: [
      'Streamlined medical imaging workflows',
      'AI-assisted anomaly flagging for clinical teams',
      'Faster turnaround for analysis tasks',
      'Scalable deployment across departments',
    ],
    challenge:
      'Clinical teams faced high volumes of imaging data with limited bandwidth, leading to delays in analysis and increased workload pressure.',
    solution:
      'Developed an AI-assisted platform that analyses medical images, surfaces key findings, and supports clinical decision-making — reducing manual effort and speeding up workflows.',
    stack: ['Computer Vision', 'Deep Learning', 'Web Application', 'Cloud Infrastructure'],
  },

  4: {
    client: 'Infrastructure Organisation',
    industry: 'Smart Infrastructure',
    projectType: 'Visual Monitoring & Event Detection System',
    outcome: [
      'Real-time monitoring across distributed locations',
      'Automated event detection and alerts',
      'Improved situational awareness for operators',
      'Scalable deployment on existing camera infrastructure',
    ],
    challenge:
      'The organisation needed to monitor large, distributed environments in real time but lacked the tools to detect events automatically and respond quickly.',
    solution:
      'Deployed an edge-based visual monitoring platform with automated event detection, enabling real-time alerts and centralised visibility across all monitored sites.',
    stack: ['Computer Vision', 'Edge AI', 'IoT', 'Cloud Monitoring Platform'],
  },

  5: {
    client: 'Financial Services Organisation',
    industry: 'Financial Services',
    projectType: 'Enterprise Knowledge & Search Platform',
    outcome: [
      'Faster retrieval of information across document repositories',
      'Reduced time spent searching for knowledge',
      'Improved collaboration and knowledge sharing',
      'Scalable to growing document volumes',
    ],
    challenge:
      'Teams struggled to find relevant information quickly across large and fragmented knowledge bases, impacting productivity and response times.',
    solution:
      'Implemented an AI-powered semantic search and knowledge platform that understands natural language queries and surfaces the most relevant content instantly.',
    stack: ['NLP', 'Generative AI', 'Search Engine', 'Cloud Platform'],
  },

  6: {
    client: 'Agricultural Organisation',
    industry: 'Agriculture',
    projectType: 'Crop Health Monitoring Solution',
    outcome: [
      'Early identification of crop health issues',
      'Improved field visibility for farm managers',
      'Timely intervention to protect yield',
      'Scalable monitoring across multiple field sites',
    ],
    challenge:
      'Identifying crop health issues early was difficult with traditional field inspection methods, leading to delayed responses and preventable yield losses.',
    solution:
      'Built an AI-driven monitoring solution that analyses field imagery to detect signs of crop stress or disease early, providing actionable alerts to farm teams.',
    stack: ['Computer Vision', 'AI/ML', 'Mobile Application', 'Cloud Services'],
  },

  7: {
    client: 'Distribution Organisation',
    industry: 'Supply Chain & Distribution',
    projectType: 'Demand Forecasting & Planning Platform',
    outcome: [
      'More accurate demand forecasting',
      'Better inventory planning and stock management',
      'Reduced operational waste from over/under-stocking',
      'Improved coordination across the supply chain',
    ],
    challenge:
      'Demand planning relied heavily on manual processes and historical assumptions, resulting in frequent inventory imbalances and planning inefficiencies.',
    solution:
      'Developed a predictive analytics platform that combines operational data with external signals to generate reliable demand forecasts and support planning decisions.',
    stack: ['Machine Learning', 'Analytics', 'Web Dashboard', 'Data Integration'],
  },

  8: {
    client: 'Industrial Organisation',
    industry: 'Industrial Operations',
    projectType: 'Predictive Maintenance & Scheduling Platform',
    outcome: [
      'Earlier detection of potential equipment failures',
      'Improved maintenance planning and scheduling',
      'Reduced unplanned downtime and disruptions',
      'Better visibility into equipment health',
    ],
    challenge:
      'Unexpected equipment failures disrupted operations and were costly to address reactively, with little visibility into emerging issues before they escalated.',
    solution:
      'Implemented an AI-powered maintenance platform that continuously monitors equipment data, detects anomalies, and enables maintenance teams to act proactively before failures occur.',
    stack: ['IoT', 'Machine Learning', 'Predictive Analytics', 'Cloud Monitoring Platform'],
  },
};

interface Props {
  project: ProjectItem;
  onClose: () => void;
}

const ProjectModal: React.FC<Props> = ({ project, onClose }) => {
  const cs = caseStudies[project.id];

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="pmodal__overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pmodal" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className={`pmodal__hero ${project.bgClass}`}>
          <span className="pmodal__emoji">{project.emoji}</span>
          <button className="pmodal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="pmodal__body">

          {/* Categories */}
          <div className="pmodal__cats">
            {project.categories.map(c => (
              <span className="project-cat" key={c.label}>{c.label}</span>
            ))}
          </div>

          <h2 className="pmodal__title">{project.title}</h2>

          {/* Meta row */}
          {cs && (
            <div className="pmodal__meta">
              <div className="pmodal__meta-item">
                <span className="pmodal__meta-label">Industry</span>
                <span className="pmodal__meta-val">{cs.industry}</span>
              </div>
              <div className="pmodal__meta-item">
                <span className="pmodal__meta-label">Solution</span>
                <span className="pmodal__meta-val">{cs.projectType}</span>
              </div>
            </div>
          )}

          {/* Outcomes */}
          {cs && (
            <div className="pmodal__section">
              <h4 className="pmodal__section-title">Key Outcomes</h4>
              <ul className="pmodal__outcomes">
                {cs.outcome.map(o => (
                  <li key={o} className="pmodal__outcome-item">
                    <span className="pmodal__outcome-tick">✓</span> {o}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Challenge */}
          {cs && (
            <div className="pmodal__section">
              <h4 className="pmodal__section-title">The Challenge</h4>
              <p className="pmodal__text">{cs.challenge}</p>
            </div>
          )}

          {/* Solution */}
          {cs && (
            <div className="pmodal__section">
              <h4 className="pmodal__section-title">Our Solution</h4>
              <p className="pmodal__text">{cs.solution}</p>
            </div>
          )}

          {/* Tech stack */}
          {cs && (
            <div className="pmodal__section">
              <h4 className="pmodal__section-title">Tech Stack</h4>
              <div className="pmodal__stack">
                {cs.stack.map(t => (
                  <span className="pmodal__stack-tag" key={t}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <a href="#contact" className="pmodal__cta" onClick={onClose}>
            Start a Similar Project →
          </a>

        </div>
      </div>
    </div>
  );
};

export default ProjectModal;